import { Component, Input, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CustomInputComponent } from '../../../shared/custom-input/custom-input.component';
import { CustomButtonComponent } from '../../../shared/custom-button/custom-button.component';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ValidationService } from '../../../services/validation/validation.service';
import { AuthService } from '../../../services/auth/auth.service';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-update-password',
  standalone: true,
  imports: [
    CustomInputComponent,
    CustomButtonComponent,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatIconModule
  ],
  templateUrl: './update-password.component.html',
  styleUrl: './update-password.component.scss',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class UpdatePasswordComponent implements OnInit {
  ELEMENT_IDS: { [key: string]: string } = ELEMENT_IDS;
  @Input() username: string | null = null;
  @Input() temporaryPassword: string | null = null;

  formUpdatePassword: FormGroup;
  loading: boolean = false;
  alreadyHaveVerificationCode: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;
  passwordRequirements: any[] = [];
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder, 
    private router: Router,
    public validationService: ValidationService,
    private authService: AuthService
  ) {
    this.passwordRequirements = validationService.passwordRequirements;

    this.formUpdatePassword = this.fb.group(
      {
        newPassword: [
          null, [
            Validators.required,
            Validators.minLength(8),
            this.validationService.uppercaseLowercaseValidator(),
            this.validationService.numberValidator(),
            this.validationService.specialCharValidator(),
          ]
        ],
        confirmPassword: [
          null, [
            Validators.required
          ]
        ],
      },
      { validators: this.validationService.passwordMatchValidator }
    );

    this.formUpdatePassword.get('newPassword')?.valueChanges.subscribe(() => {
      this.validationService.updatePasswordRequirements(this.formUpdatePassword);
    });

    this.formUpdatePassword.valueChanges.subscribe(() => {
      this.validationService.updatePasswordRequirements(this.formUpdatePassword);
    });
  }

  ngOnInit(): void {
    if (!this.username) {
      this.onBackToLogin();
    }
  }

  onSubmit() {
    if (!this.formUpdatePassword.valid) return;
    this.loading = true;

    this.authService.changePassword(
      this.username!,
      this.temporaryPassword!,
      this.formUpdatePassword.get('newPassword')?.value,
      false
    ).then(
      () => {
        this.loading = false;
        this.router.navigate(['/'], {});
      },
      (error) => {
        this.loading = false;
        this.errorMessage = "Error changing password"
      }
    );
  }

  onClose() {}

  onHasVerificationCode() {
    this.alreadyHaveVerificationCode = true;
  }

  onBackToLogin() {
    this.router.navigate(['/login'], {});
  }

  toggleShowPassword(whichPassword: 'new-password' | 'confirm-password') {
    if (whichPassword === 'new-password') {
      this.showNewPassword = !this.showNewPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }
}

const BASE_ELEMENT_ID: string = 'id_update_password';
const ELEMENT_IDS: { [key: string]: string } = {
  page: `${BASE_ELEMENT_ID}_page`,
  lbl_title: `${BASE_ELEMENT_ID}_lbl_title`,
  lbl_subtitle: `${BASE_ELEMENT_ID}_lbl_subtitle`,
  form: `${BASE_ELEMENT_ID}_form`,
  input_new_password: `${BASE_ELEMENT_ID}_input_new_password`,
  btn_toggle_new_password: `${BASE_ELEMENT_ID}_btn_toggle_new_password`,
  input_confirm_password: `${BASE_ELEMENT_ID}_input_confirm_password`,
  btn_toggle_confirm_password: `${BASE_ELEMENT_ID}_btn_toggle_confirm_password`,
  msg_error: `${BASE_ELEMENT_ID}_msg_error`,
  lst_requirements: `${BASE_ELEMENT_ID}_lst_requirements`,
  lst_requirements_item: `${BASE_ELEMENT_ID}_lst_requirements_item`,
  lst_requirements_icon: `${BASE_ELEMENT_ID}_lst_requirements_icon`,
  lst_requirements_lbl: `${BASE_ELEMENT_ID}_lst_requirements_lbl`,
  btn_update_password: `${BASE_ELEMENT_ID}_btn_update_password`,
  btn_back: `${BASE_ELEMENT_ID}_btn_back`,
};
